/**
 * Parser incremental de `text/event-stream`.
 *
 * El stream llega en chunks arbitrarios: un evento puede partirse al medio de
 * una línea y varios eventos pueden venir en el mismo chunk. El parser guarda
 * lo que quedó incompleto y solo emite eventos cerrados (línea en blanco).
 *
 * Formato (contrato §3.3):
 *   event: result
 *   data: {"compania":"..."}
 *   <línea en blanco>
 */

export interface SseEvent {
  /** Nombre del evento (`event:`). Si falta, "message" como manda el estándar. */
  event: string;
  /** Payload crudo (todas las líneas `data:` unidas por \n). */
  data: string;
}

export class SseParser {
  private buffer = '';

  /** Consume un chunk y devuelve los eventos completos que se cerraron. */
  push(chunk: string): SseEvent[] {
    this.buffer += chunk;
    const eventos: SseEvent[] = [];

    // Separador de eventos: línea en blanco (\n\n, \r\n\r\n o \r\r).
    // Se normalizan los fines de línea antes de cortar.
    this.buffer = this.buffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    let corte = this.buffer.indexOf('\n\n');
    while (corte !== -1) {
      const bloque = this.buffer.slice(0, corte);
      this.buffer = this.buffer.slice(corte + 2);
      const evento = parseBloque(bloque);
      if (evento) eventos.push(evento);
      corte = this.buffer.indexOf('\n\n');
    }
    return eventos;
  }

  /**
   * Cierra el stream: si quedó un bloque sin línea en blanco final, lo emite.
   * Algunos servidores no mandan el \n\n del último evento antes de cerrar.
   */
  flush(): SseEvent[] {
    const resto = this.buffer;
    this.buffer = '';
    const evento = parseBloque(resto);
    return evento ? [evento] : [];
  }
}

const parseBloque = (bloque: string): SseEvent | null => {
  const lineas = bloque.split('\n');
  let event = '';
  const datos: string[] = [];

  lineas.forEach((linea) => {
    if (!linea || linea.startsWith(':')) return; // comentario / keep-alive
    const sep = linea.indexOf(':');
    const campo = sep === -1 ? linea : linea.slice(0, sep);
    // El estándar saca UN espacio después de los dos puntos.
    let valor = sep === -1 ? '' : linea.slice(sep + 1);
    if (valor.startsWith(' ')) valor = valor.slice(1);

    if (campo === 'event') event = valor;
    else if (campo === 'data') datos.push(valor);
  });

  if (!event && datos.length === 0) return null;
  return { event: event || 'message', data: datos.join('\n') };
};

/** Parsea el `data` de un evento como JSON; null si no es JSON válido. */
export const parseSseData = <T>(data: string): T | null => {
  if (!data) return null;
  try {
    return JSON.parse(data) as T;
  } catch {
    return null;
  }
};
